import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, formatPrice, imageUrl, uiText } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag } from "lucide-react";

export default function ProductPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { addItem, items } = useCart();
    const { data: p, isLoading } = useQuery({ queryKey: ["product", id], queryFn: async () => (await api.get(`/products/${id}`)).data });
    const { data: settings } = useQuery({ queryKey: ["settings-product"], queryFn: async () => (await api.get("/settings")).data });
    const [size, setSize] = useState("");
    const [color, setColor] = useState("");
    const [qty, setQty] = useState(1);
    const [imgIdx, setImgIdx] = useState(0);

    if (isLoading) return <div className="py-24 text-center text-sm text-ink/50">Cargando…</div>;
    if (!p) return <div className="py-24 text-center"><p>Producto no encontrado</p><Link to="/tienda" className="btn-outline mt-4 inline-block">Volver a la tienda</Link></div>;

    const discount = p.previousPrice ? Math.round((1 - p.price / p.previousPrice) * 100) : 0;
    const canAdd = (p.sizes.length === 0 || size) && (p.colors.length === 0 || color);
    const sizeStockMap = p.size_stock || {};
    const colorStockMap = p.color_stock || {};
    const availFor = (s) => (s && s in sizeStockMap) ? Number(sizeStockMap[s] ?? 0) : Infinity;
    const availColorFor = (c) => (c && c in colorStockMap) ? Number(colorStockMap[c] ?? 0) : Infinity;
    const maxAvail = Math.min(availFor(size), availColorFor(color), p.stock);

    const add = (goCart = false) => {
        if (p.stock <= 0) { toast.error(uiText(settings, "toast_soldout")); return; }
        if (!canAdd) { toast.error(uiText(settings, "toast_choose_variant")); return; }
        const inCart = items
            .filter((i) => i.productId === p.id && i.size === (size || "Única") && i.color === (color || "Único"))
            .reduce((s, i) => s + i.quantity, 0);
        if (inCart + qty > maxAvail) {
            toast.error(`Solo hay ${maxAvail} unidades disponibles${size ? ` en talla ${size}` : ""}${inCart ? ` (ya tenés ${inCart} en el carrito)` : ""}`);
            return;
        }
        addItem(p, size || "Única", color || "Único", qty);
        toast.success(uiText(settings, "toast_added"));
        if (goCart) nav("/carrito");
    };

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-10">
            <div className="grid lg:grid-cols-2 gap-10">
                <div>
                    <div className="relative aspect-[4/5] rounded-md overflow-hidden bg-sandLight">
                        <img src={imageUrl(p.images?.[imgIdx])} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    {p.images?.length > 1 && (
                        <div className="flex gap-2 mt-3">
                            {p.images.map((im, i) => (
                                <button key={i} onClick={() => setImgIdx(i)} className={`w-20 aspect-square rounded overflow-hidden border-2 ${imgIdx === i ? "border-ink" : "border-transparent"}`}>
                                    <img src={imageUrl(im)} className="w-full h-full object-cover" alt="" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <p className="text-eyebrow">{p.brand}</p>
                    {p.sku && <p className="text-xs text-ink/40 mt-1">Código: {p.sku}</p>}
                    <h1 className="font-display text-3xl sm:text-4xl text-ink mt-2">{p.name}</h1>
                    <div className="flex items-baseline gap-3 mt-4">
                        <span className="text-2xl text-ink">{formatPrice(p.price)}</span>
                        {p.previousPrice && <span className="text-sm text-ink/40 line-through">{formatPrice(p.previousPrice)}</span>}
                        {discount > 0 && <span className="text-xs bg-terracotta text-white px-2 py-1 rounded-full">-{discount}%</span>}
                    </div>
                    <p className="text-sm text-ink/70 mt-6 leading-relaxed">{p.description}</p>

                    {p.sizes.length > 0 && (
                        <div className="mt-6">
                            <p className="text-eyebrow mb-2">Talla</p>
                            <div className="flex flex-wrap gap-2">
                                {p.sizes.map((s) => (
                                    <button key={s} onClick={() => { setSize(s); setQty(1); }} disabled={availFor(s) <= 0} data-testid={`size-${s}`} className={`notranslate px-4 py-2 border text-sm rounded-full ${availFor(s) <= 0 ? "opacity-40 line-through cursor-not-allowed" : size === s ? "border-ink bg-ink text-bone" : "border-sandLight hover:border-ink"}`} translate="no">{s}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    {p.colors.length > 0 && (
                        <div className="mt-6">
                            <p className="text-eyebrow mb-2">Color</p>
                            <div className="flex flex-wrap gap-2">
                                {p.colors.map((c) => (
                                    <button key={c} onClick={() => { setColor(c); setQty(1); }} disabled={availColorFor(c) <= 0} data-testid={`color-${c}`} className={`notranslate px-4 py-2 border text-sm rounded-full ${availColorFor(c) <= 0 ? "opacity-40 line-through cursor-not-allowed" : color === c ? "border-ink bg-ink text-bone" : "border-sandLight hover:border-ink"}`} translate="no">{c}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex items-center gap-4">
                        <div className="inline-flex items-center border border-sandLight rounded-full">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                            <span className="w-8 text-center text-sm">{qty}</span>
                            <button onClick={() => setQty(Math.min(maxAvail, qty + 1))} disabled={qty >= maxAvail} data-testid="qty-plus" className="w-9 h-9 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"><Plus className="w-3 h-3" /></button>
                        </div>
                        <p className="text-xs text-ink/60">{size && size in sizeStockMap ? `Stock talla ${size}: ${availFor(size)}` : `Stock: ${p.stock}`}</p>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <button onClick={() => add(false)} data-testid={`btn-add-to-cart-${p.id}`} className={`btn-outline inline-flex items-center gap-2 justify-center flex-1 ${!canAdd || p.stock <= 0 ? "opacity-60" : ""}`}><ShoppingBag className="w-4 h-4" />Agregar al carrito</button>
                        <button onClick={() => add(true)} data-testid={`btn-buy-now-${p.id}`} className={`btn-ink flex-1 ${!canAdd || p.stock <= 0 ? "opacity-60" : ""}`}>Comprar Ahora</button>
                    </div>
                    {p.stock <= 0 && <p className="text-sm text-terracotta mt-4">Producto agotado</p>}
                </div>
            </div>
        </div>
    );
}
